import { defineStore } from 'pinia'
import type { Coupon } from '~/composables/types/checkout'
import type { Price } from '~/composables/types/event'

export interface CouponValidateResponse {
  coupon: Coupon
  discount: null | any
}

export const useSessionVoucherStore = defineStore('sessionVoucher', () => {
  const sessionVoucherCookie = useCookie('sessionVoucher')
  const { $i18n } = useNuxtApp()
  const { locale } = $i18n
  const voucherData = ref<Coupon | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)
  const { cart } = useCartWithSubdomainLogic()

  const getDiscountAmount = (coupon: Coupon) => {
    if (coupon?.discount_type === 'FIXED_VALUE') {
      return formatPriceAmount(
        Number((coupon.value as Price).amount),
        locale.value
      )
    } else if (coupon?.discount_type === 'PERCENTAGE_VALUE') {
      return coupon.value + '%'
    }
    return undefined
  }

  const validateVoucher = async (voucherCode: string) => {
    if (!process.client) return

    loading.value = true
    error.value = null

    try {
      const response = await kftApiRequest<CouponValidateResponse>(
        `v1/store/coupon/${voucherCode}/validate`
      )

      const coupon = response?.data?.coupon

      if (!coupon || !coupon.value) {
        throw new Error("Coupon doesn't exist or has no value")
      }

      voucherData.value = coupon
      sessionVoucherCookie.value = voucherCode
      return coupon
    } catch (e: any) {
      error.value = e?.data?.message || 'Failed to validate voucher'
      throw e
    } finally {
      loading.value = false
    }
  }

  const clearVoucher = () => {
    sessionVoucherCookie.value = null
    voucherData.value = null
    error.value = null
  }

  // Initialize voucher data if cookie exists
  if (process.client && sessionVoucherCookie.value) {
    validateVoucher(sessionVoucherCookie.value)
  }

  const minimumAmountForCoupon = computed(() => {
    if (!voucherData.value) return 0
    return Number(voucherData.value.minimum_order_value?.amount) / 100
  })

  const couponError = computed(() => {
    if (!voucherData.value) return null

    const cartTotal = cartGetters.getTotals(cart.value).total

    if (
      minimumAmountForCoupon.value &&
      cartTotal < minimumAmountForCoupon.value
    ) {
      return 'minimum-not-reached'
    }
    return null
  })

  return {
    voucherCode: computed(() => sessionVoucherCookie.value),
    voucherData: computed(() => voucherData.value),
    loading: computed(() => loading.value),
    error: computed(() => error.value),
    minimumAmountForCoupon,
    couponError,
    validateVoucher,
    clearVoucher,
    getDiscountAmount,
  }
})
